import { type ApiRequestService } from '@coris-ts/repository/data-source/api-request.service'
import { Cached } from '@coris-ts/helpers/utilities'
import { GetRepositoryMapper } from '@coris-ts/repository/repository-mapper'
import {
  provideDefaultNetworkDataSource,
  provideDefaultArrayNetworkDataSource
} from '@coris-ts/repository/data-source/network.data-source'
import { SingleGetDataSourceRepository } from '@coris-ts/repository/single-data-source.repository'
import { GetInteractor } from '@coris-ts/repository/interactor/get.interactor'
import { MediaPageEntity, MediaTotalsEntity } from './data/entities/media.entity'
import { EventsPageEntity, EventsTotalsEntity } from './data/entities/events.entity'
import {
  CommunityPostsPageEntity,
  CommunityChannelEntity,
  CommunityTotalsEntity
} from './data/entities/community.entity'
import {
  UsersMediaPageEntity,
  UsersEventsPageEntity,
  UsersForumPageEntity,
  UsersChatPageEntity,
  UsersTotalsEntity
} from './data/entities/users.entity'
import { ChatChannelEntity, ChatTotalsEntity } from './data/entities/chat.entity'
import { SectionsComparisonEntity, SectionsSummaryEntity } from './data/entities/sections.entity'
import { GetSongsInteractor } from './domain/interactors/get-songs.interactor'
import { GetVideosInteractor } from './domain/interactors/get-videos.interactor'
import { GetEventsInteractor } from './domain/interactors/get-events.interactor'
import { GetCommunityInteractor } from './domain/interactors/get-community.interactor'
import { GetChatInteractor } from './domain/interactors/get-chat.interactor'
import { GetUsersMediaInteractor } from './domain/interactors/get-users-media.interactor'
import { GetUsersEventsInteractor } from './domain/interactors/get-users-events.interactor'
import { GetUsersForumInteractor } from './domain/interactors/get-users-forum.interactor'
import { GetUsersChatInteractor } from './domain/interactors/get-users-chat.interactor'
import { GetUsersTotalsInteractor } from './domain/interactors/get-users-totals.interactor'
import { GetSectionsSummaryInteractor } from './domain/interactors/get-sections-summary.interactor'
import { GetSectionsComparisonInteractor } from './domain/interactors/get-sections-comparison.interactor'

export interface EngagementComponent {
  provideGetSongs: () => GetSongsInteractor
  provideGetVideos: () => GetVideosInteractor
  provideGetEvents: () => GetEventsInteractor
  provideGetCommunity: () => GetCommunityInteractor
  provideGetChat: () => GetChatInteractor
  provideGetUsersMedia: () => GetUsersMediaInteractor
  provideGetUsersEvents: () => GetUsersEventsInteractor
  provideGetUsersForum: () => GetUsersForumInteractor
  provideGetUsersChat: () => GetUsersChatInteractor
  provideGetUsersTotals: () => GetUsersTotalsInteractor
  provideGetSectionsSummary: () => GetSectionsSummaryInteractor
  provideGetSectionsComparison: () => GetSectionsComparisonInteractor
}

export class EngagementDefaultModule implements EngagementComponent {
  constructor(private readonly apiRequestService: ApiRequestService) {}

  @Cached()
  private getMediaPageRepository(): GetRepositoryMapper<MediaPageEntity, MediaPageEntity> {
    const dataSource = provideDefaultNetworkDataSource(this.apiRequestService, MediaPageEntity)
    const repository = new SingleGetDataSourceRepository(dataSource)
    return new GetRepositoryMapper(repository, { map: (entity: MediaPageEntity) => entity })
  }

  @Cached()
  private getMediaTotalsRepository(): GetRepositoryMapper<MediaTotalsEntity, MediaTotalsEntity> {
    const dataSource = provideDefaultNetworkDataSource(this.apiRequestService, MediaTotalsEntity)
    const repository = new SingleGetDataSourceRepository(dataSource)
    return new GetRepositoryMapper(repository, { map: (entity: MediaTotalsEntity) => entity })
  }

  @Cached()
  private getEventsPageRepository(): GetRepositoryMapper<EventsPageEntity, EventsPageEntity> {
    const dataSource = provideDefaultNetworkDataSource(this.apiRequestService, EventsPageEntity)
    const repository = new SingleGetDataSourceRepository(dataSource)
    return new GetRepositoryMapper(repository, { map: (entity: EventsPageEntity) => entity })
  }

  @Cached()
  private getEventsTotalsRepository(): GetRepositoryMapper<EventsTotalsEntity, EventsTotalsEntity> {
    const dataSource = provideDefaultNetworkDataSource(this.apiRequestService, EventsTotalsEntity)
    const repository = new SingleGetDataSourceRepository(dataSource)
    return new GetRepositoryMapper(repository, { map: (entity: EventsTotalsEntity) => entity })
  }

  @Cached()
  private getCommunityPostsRepository(): GetRepositoryMapper<CommunityPostsPageEntity, CommunityPostsPageEntity> {
    const dataSource = provideDefaultNetworkDataSource(this.apiRequestService, CommunityPostsPageEntity)
    const repository = new SingleGetDataSourceRepository(dataSource)
    return new GetRepositoryMapper(repository, { map: (entity: CommunityPostsPageEntity) => entity })
  }

  @Cached()
  private getCommunityChannelsRepository(): GetRepositoryMapper<CommunityChannelEntity[], CommunityChannelEntity[]> {
    const dataSource = provideDefaultArrayNetworkDataSource(this.apiRequestService, CommunityChannelEntity)
    const repository = new SingleGetDataSourceRepository(dataSource)
    return new GetRepositoryMapper(repository, { map: (entity: CommunityChannelEntity[]) => entity })
  }

  @Cached()
  private getCommunityTotalsRepository(): GetRepositoryMapper<CommunityTotalsEntity, CommunityTotalsEntity> {
    const dataSource = provideDefaultNetworkDataSource(this.apiRequestService, CommunityTotalsEntity)
    const repository = new SingleGetDataSourceRepository(dataSource)
    return new GetRepositoryMapper(repository, { map: (entity: CommunityTotalsEntity) => entity })
  }

  @Cached()
  private getChatChannelsRepository(): GetRepositoryMapper<ChatChannelEntity[], ChatChannelEntity[]> {
    const dataSource = provideDefaultArrayNetworkDataSource(this.apiRequestService, ChatChannelEntity)
    const repository = new SingleGetDataSourceRepository(dataSource)
    return new GetRepositoryMapper(repository, { map: (entity: ChatChannelEntity[]) => entity })
  }

  @Cached()
  private getChatTotalsRepository(): GetRepositoryMapper<ChatTotalsEntity, ChatTotalsEntity> {
    const dataSource = provideDefaultNetworkDataSource(this.apiRequestService, ChatTotalsEntity)
    const repository = new SingleGetDataSourceRepository(dataSource)
    return new GetRepositoryMapper(repository, { map: (entity: ChatTotalsEntity) => entity })
  }

  @Cached()
  private getUsersMediaRepository(): GetRepositoryMapper<UsersMediaPageEntity, UsersMediaPageEntity> {
    const dataSource = provideDefaultNetworkDataSource(this.apiRequestService, UsersMediaPageEntity)
    const repository = new SingleGetDataSourceRepository(dataSource)
    return new GetRepositoryMapper(repository, { map: (entity: UsersMediaPageEntity) => entity })
  }

  @Cached()
  private getUsersEventsRepository(): GetRepositoryMapper<UsersEventsPageEntity, UsersEventsPageEntity> {
    const dataSource = provideDefaultNetworkDataSource(this.apiRequestService, UsersEventsPageEntity)
    const repository = new SingleGetDataSourceRepository(dataSource)
    return new GetRepositoryMapper(repository, { map: (entity: UsersEventsPageEntity) => entity })
  }

  @Cached()
  private getUsersForumRepository(): GetRepositoryMapper<UsersForumPageEntity, UsersForumPageEntity> {
    const dataSource = provideDefaultNetworkDataSource(this.apiRequestService, UsersForumPageEntity)
    const repository = new SingleGetDataSourceRepository(dataSource)
    return new GetRepositoryMapper(repository, { map: (entity: UsersForumPageEntity) => entity })
  }

  @Cached()
  private getUsersChatRepository(): GetRepositoryMapper<UsersChatPageEntity, UsersChatPageEntity> {
    const dataSource = provideDefaultNetworkDataSource(this.apiRequestService, UsersChatPageEntity)
    const repository = new SingleGetDataSourceRepository(dataSource)
    return new GetRepositoryMapper(repository, { map: (entity: UsersChatPageEntity) => entity })
  }

  @Cached()
  private getUsersTotalsRepository(): GetRepositoryMapper<UsersTotalsEntity, UsersTotalsEntity> {
    const dataSource = provideDefaultNetworkDataSource(this.apiRequestService, UsersTotalsEntity)
    const repository = new SingleGetDataSourceRepository(dataSource)
    return new GetRepositoryMapper(repository, { map: (entity: UsersTotalsEntity) => entity })
  }

  @Cached()
  private getSectionsSummaryRepository(): GetRepositoryMapper<SectionsSummaryEntity, SectionsSummaryEntity> {
    const dataSource = provideDefaultNetworkDataSource(this.apiRequestService, SectionsSummaryEntity)
    const repository = new SingleGetDataSourceRepository(dataSource)
    return new GetRepositoryMapper(repository, { map: (entity: SectionsSummaryEntity) => entity })
  }

  @Cached()
  private getSectionsComparisonRepository(): GetRepositoryMapper<SectionsComparisonEntity, SectionsComparisonEntity> {
    const dataSource = provideDefaultNetworkDataSource(this.apiRequestService, SectionsComparisonEntity)
    const repository = new SingleGetDataSourceRepository(dataSource)
    return new GetRepositoryMapper(repository, { map: (entity: SectionsComparisonEntity) => entity })
  }

  public provideGetSongs(): GetSongsInteractor {
    return new GetSongsInteractor(
      new GetInteractor<MediaPageEntity>(this.getMediaPageRepository()),
      new GetInteractor<MediaTotalsEntity>(this.getMediaTotalsRepository())
    )
  }

  public provideGetVideos(): GetVideosInteractor {
    return new GetVideosInteractor(
      new GetInteractor<MediaPageEntity>(this.getMediaPageRepository()),
      new GetInteractor<MediaTotalsEntity>(this.getMediaTotalsRepository())
    )
  }

  public provideGetEvents(): GetEventsInteractor {
    return new GetEventsInteractor(
      new GetInteractor<EventsPageEntity>(this.getEventsPageRepository()),
      new GetInteractor<EventsTotalsEntity>(this.getEventsTotalsRepository())
    )
  }

  public provideGetCommunity(): GetCommunityInteractor {
    return new GetCommunityInteractor(
      new GetInteractor<CommunityPostsPageEntity>(this.getCommunityPostsRepository()),
      new GetInteractor<CommunityChannelEntity[]>(this.getCommunityChannelsRepository()),
      new GetInteractor<CommunityTotalsEntity>(this.getCommunityTotalsRepository())
    )
  }

  public provideGetChat(): GetChatInteractor {
    return new GetChatInteractor(
      new GetInteractor<ChatChannelEntity[]>(this.getChatChannelsRepository()),
      new GetInteractor<ChatTotalsEntity>(this.getChatTotalsRepository())
    )
  }

  public provideGetUsersMedia(): GetUsersMediaInteractor {
    return new GetUsersMediaInteractor(new GetInteractor<UsersMediaPageEntity>(this.getUsersMediaRepository()))
  }

  public provideGetUsersEvents(): GetUsersEventsInteractor {
    return new GetUsersEventsInteractor(new GetInteractor<UsersEventsPageEntity>(this.getUsersEventsRepository()))
  }

  public provideGetUsersForum(): GetUsersForumInteractor {
    return new GetUsersForumInteractor(new GetInteractor<UsersForumPageEntity>(this.getUsersForumRepository()))
  }

  public provideGetUsersChat(): GetUsersChatInteractor {
    return new GetUsersChatInteractor(new GetInteractor<UsersChatPageEntity>(this.getUsersChatRepository()))
  }

  public provideGetUsersTotals(): GetUsersTotalsInteractor {
    return new GetUsersTotalsInteractor(new GetInteractor<UsersTotalsEntity>(this.getUsersTotalsRepository()))
  }

  public provideGetSectionsSummary(): GetSectionsSummaryInteractor {
    return new GetSectionsSummaryInteractor(
      new GetInteractor<SectionsSummaryEntity>(this.getSectionsSummaryRepository())
    )
  }

  public provideGetSectionsComparison(): GetSectionsComparisonInteractor {
    return new GetSectionsComparisonInteractor(
      new GetInteractor<SectionsComparisonEntity>(this.getSectionsComparisonRepository())
    )
  }
}
